import type {
  AnchorEvent,
  ApprovalEvent,
  BranchEvent,
  CheckpointEvent,
  CompactionEvent,
  ErrorEvent,
  InfoEvent,
  InputEvent,
  InterruptEvent,
  JsonChange,
  LoggerEvent,
  ModelEvent,
  ReviewEvent,
  SampleInitEvent,
  SampleLimitEvent,
  SandboxEvent,
  ScoreEditEvent,
  ScoreEvent,
  SentinelEvent,
  SpanBeginEvent,
  SpanEndEvent,
  StateEvent,
  StepEvent,
  StoreEvent,
  SubtaskEvent,
  ToolEvent,
} from "@tsmono/inspect-common/types";

import type {
  ScreenshotContent,
  ToolAnnotation,
} from "../chat/tools/browserActionUtils";

import type { MakeCiteUrl } from "./citeReferences";
import type { SentinelStep, ToolSentinels } from "./transform/toolSentinels";
import { SPAN_BEGIN, STEP, TYPE_SUBTASK, TYPE_TOOL } from "./transform/utils";

export interface StateManager {
  scope: string;
  getState(): object;
  initializeState(state: object): void;
  applyChanges(changes: JsonChange[]): object;
}

export const kTranscriptCollapseScope = "transcript-collapse";
export const kTranscriptOutlineCollapseScope = "transcript-outline";

/** Event types whose *children* can be collapsed (tree collapse). */
export const kCollapsibleEventTypes = [
  STEP,
  SPAN_BEGIN,
  TYPE_TOOL,
  TYPE_SUBTASK,
];

export type CollapsibleEvent =
  StepEvent | SpanBeginEvent | ToolEvent | SubtaskEvent;

export const isCollapsibleEvent = (
  event: EventType
): event is CollapsibleEvent =>
  kCollapsibleEventTypes.some((type) => type === event.event);

/** Event types whose *content* can be collapsed (panel-level collapse). */
export const kContentCollapsibleEventTypes: string[] = [
  "model",
  "state",
  "store",
];

export type EventType =
  | SampleInitEvent
  | SampleLimitEvent
  | StateEvent
  | StoreEvent
  | ModelEvent
  | LoggerEvent
  | InfoEvent
  | StepEvent
  | SubtaskEvent
  | ScoreEvent
  | ScoreEditEvent
  | ToolEvent
  | InputEvent
  | InterruptEvent
  | ErrorEvent
  | AnchorEvent
  | ApprovalEvent
  | ReviewEvent
  | SentinelEvent
  | BranchEvent
  | CheckpointEvent
  | CompactionEvent
  | SandboxEvent
  | SpanBeginEvent
  | SpanEndEvent;

// Define the runtime array of all event type values
export const eventTypeValues = [
  "sample_init",
  "sample_limit",
  "state",
  "store",
  "model",
  "logger",
  "info",
  "step",
  "subtask",
  "score",
  "score_edit",
  "tool",
  "input",
  "interrupt",
  "error",
  "anchor",
  "approval",
  "review",
  "sentinel",
  "branch",
  "checkpoint",
  "compaction",
  "sandbox",
  "span_begin",
  "span_end",
] as const;

// Event types the transcript hides by default (the "Events: Default" filter).
export const kDefaultExcludeEvents: readonly EventTypeValue[] = [
  "sample_init",
  "sandbox",
  "state",
  "store",
  "branch",
  "anchor",
  "checkpoint",
];

// Derive the type from the array (replaces the indexed access approach)
export type EventTypeValue = (typeof eventTypeValues)[number];

/**
 * Minimal span info attached to event nodes by timeline processing.
 * Set by scout's `attachSourceSpans()`; undefined in inspect.
 */
export interface EventNodeSpan {
  spanType: string | null;
  name: string;
  description?: string;
}

export class EventNode<T extends EventType = EventType> {
  id: string;
  event: T;
  children: EventNode<EventType>[] = [];
  depth: number;
  /** Timeline span info, attached by app-level processing. */
  sourceSpan?: EventNodeSpan;

  constructor(id: string, event: T, depth: number) {
    this.id = id;
    this.event = event;
    this.depth = depth;
  }
}

const isEventNodeOf = <T extends EventType["event"]>(
  node: EventNode,
  type: T
): node is EventNode<Extract<EventType, { event: T }>> =>
  node.event.event === type;

/**
 * Re-reads an event node's discriminant to get a node typed to it.
 *
 * A `switch (node.event.event)` narrows the event but not the `EventNode`
 * wrapping it — TypeScript cannot re-key a generic class instance from a
 * discriminant on one of its fields. One string compare per branch buys back
 * the type the case already established.
 */
export const eventNodeOf = <T extends EventType["event"]>(
  node: EventNode,
  type: T
): EventNode<Extract<EventType, { event: T }>> => {
  if (!isEventNodeOf(node, type)) {
    throw new Error(
      `expected a "${type}" event node, got "${node.event.event}"`
    );
  }
  return node;
};

/**
 * Props threaded from app-level stores through the virtual list
 * to EventPanel for collapse state and deep-link URL generation.
 */
export interface EventPanelCallbacks {
  onCollapse?: (id: string, collapsed: boolean) => void;
  getCollapsed?: (id: string) => boolean;
  getEventUrl?: (eventId: string) => string | undefined;
  linkingEnabled?: boolean;
  /** Selected tab NAME for a multi-tab event panel; when it returns a name
   *  it wins over the panel's own per-event selection. */
  getSelectedTab?: (
    eventNodeId: string,
    tabNames: string[]
  ) => string | undefined;
  /** Record a tab selection on a multi-tab event panel (focus page: writes the
   *  `?tab=` param so the selection follows turn navigation). */
  onSelectTab?: (eventNodeId: string, tabName: string) => void;
  /** A user selected one of the panel's inner tabs (never initial/programmatic
   *  selection) — the host aligns the panel to show the new tab from its start. */
  onTabSelected?: (eventNodeId: string) => void;
  /** Builds the focus-mode entry href for an event's header link, carrying
   *  the panel's selected tab. Omit to hide that control. */
  getEventFocusUrl?: (
    eventId: string,
    selectedTab?: string
  ) => string | undefined;
  /** Report the panel's selected tab NAME (undefined = default) so the `f`
   *  shortcut builds the same tab-carrying focus URL as the header link. */
  onFocusTabChange?: (eventNodeId: string, tabName: string | undefined) => void;
  /** Navigate to the turn before the given 1-based turn number (header chevron). */
  onPrevTurn?: (turnNumber: number) => void;
  /** Navigate to the turn after the given 1-based turn number (header chevron). */
  onNextTurn?: (turnNumber: number) => void;
  /** A header turn label was clicked — opens the go-to-turn bar prefilled with
   *  that turn number. When omitted the label renders as passive text. */
  onTurnLabelClick?: (turnNumber: number) => void;
  /** Whether this event card is the landing target of the latest go-to-turn
   *  jump — it renders a persistent selection ring. */
  isJumpTarget?: (eventNodeId: string) => boolean;
  /** Enter focus mode in the current window, from the SAME `getEventFocusUrl`
   *  href the anchor renders (`#`-prefixed hrefs accepted) — the anchor keeps
   *  the href so modified clicks open a new tab natively; a plain left-click
   *  calls this instead. */
  onOpenEventFocus?: (focusRoute: string) => void;
}

/**
 * Collapse state and callbacks for transcript and outline scopes.
 * Apps provide scope-specific callbacks so components don't need
 * to know about scope string constants.
 */
export interface TranscriptCollapseState {
  /** Current collapsed node IDs for the transcript event list. */
  transcript?: Record<string, boolean>;
  /** Current collapsed node IDs for the outline. */
  outline?: Record<string, boolean>;
  /** Collapse/expand a single transcript event node. */
  onCollapseTranscript?: (nodeId: string, collapsed: boolean) => void;
  /** Collapse/expand a single outline node. */
  onCollapseOutline?: (nodeId: string, collapsed: boolean) => void;
  /** Bulk-set transcript collapsed state (for collapse-all / expand-all). */
  onSetTranscriptCollapsed?: (ids: Record<string, boolean>) => void;
  /** Bulk-set outline collapsed state (for initialization). */
  onSetOutlineCollapsed?: (ids: Record<string, boolean>) => void;
}

export interface TranscriptEventState {
  selectedNav?: string;
  collapsed?: boolean;
}

export type TranscriptState = Record<string, TranscriptEventState>;

/**
 * Context passed to event view components by the virtual list renderer.
 * Merged from scout (hasToolEvents) and inspect (turnInfo).
 */
export interface EventNodeContext {
  hasToolEvents?: boolean;
  /** Ids of the tool events at this node's level. When set, a model event
   *  leaves out (from its summary) only the tool calls and tool messages
   *  these events show; when unset, `hasToolEvents` and the next node
   *  decide for all of them. */
  toolEventIds?: ReadonlySet<string>;
  /** The events that showed a model event's input tool messages are not in
   *  this list (a focused turn starts at its model event), so the summary
   *  hides them all and `toolEventIds` applies to the model's own calls only. */
  inputShownElsewhere?: boolean;
  turnInfo?: { turnNumber: number; totalTurns: number };
  /** True for the turn's first flattened event (its "capstone"). Only the
   *  capstone's header shows the turn-nav cluster while unstuck; other headers
   *  of the same turn reveal it only while pinned (`data-sticky-stuck`). */
  turnIsAnchor?: boolean;
  /** When true, event views should show inline expansion UX element. (e.g. ModelEventView shows a "Show all messages" toggle for expanding filtered input.) */
  inlineExpansionUX?: boolean;
  /** Per-message labels rendered in the chat label gutter (e.g. scanner citation cites like "M1"). Keyed by `message.id`. */
  messageLabels?: Record<string, string>;
  /** Per-event labels rendered beside transcript event rows. Keyed by `EventNode.id`. */
  eventLabels?: Record<string, string>;
  /** Per-tool labels rendered in tool event gutters. Keyed by `ToolEvent.id`. */
  toolLabels?: Record<string, string>;
  /** Approval events (every approver in an escalation chain) paired to their tool event via `call.id == ToolEvent.id`, keyed by the tool's node id. `ToolEventView` reads from this instead of scanning the tree, so paired approvals don't need to be nested as children (avoids spurious expand chevrons and duplicate flat rows). */
  toolApprovals?: Map<string, EventNode<ApprovalEvent>[]>;
  /** Sentinel steps paired to their tool event via `step_id == ToolEvent.id`, rendered as rows at the bottom of the tool panel beside the approval. */
  toolSentinels?: Map<string, ToolSentinels>;
  /** Links the cites in a sentinel check's explanation (e.g. `[M22]`) to the messages and events its references name. */
  makeCiteUrl?: MakeCiteUrl;
  /** Sentinel steps with no tool to render in, keyed by the node that hosts the step's row. */
  standaloneSentinels?: Map<string, SentinelStep>;
  /** Retry attempts paired to their successful ModelEvent via `retryAttemptKey(event)`. `ModelEventView` reads from this to render the inline retry chip and swap bodies between attempts. */
  retryAttempts?: Map<string, ModelEvent[]>;
  selfAnnotation?: ToolAnnotation;
  inputScreenshot?: ScreenshotContent[];
}
